import { type Appearance, DEFAULT_APPEARANCES } from "./appearance.ts";
import type { Atmosphere } from "./atmosphere.ts";
import type { Face } from "./face.ts";
import type { Wish } from "./judge.ts";
import {
  beanLoop,
  bell,
  bigButton,
  catHead,
  cornet,
  gourd,
  haniwaMask,
  kamakura,
  kirin,
  mug,
  onigiriMold,
  outletPlate,
  peanut,
  peanutPanel,
  pretzel,
  snowman,
  squareMug,
  stretched,
  sunglasses,
  type TargetDef,
  trousers,
  wateringCan,
  windowFrame,
} from "./targets.ts";

/** The paper a customer writes on (see ui/letter.module.css). */
export type Paper =
  | "washi"
  | "snowKids"
  | "crayon"
  | "kraft"
  | "cafe"
  | "grid"
  | "festive"
  | "letterhead"
  | "parchment"
  | "bakery"
  | "boutique"
  | "future"
  | "kitchen"
  | "archive"
  | "stitch";

/** A customer's letter asking for a piece: which target, in their words, with one wish. */
export interface RequestDef {
  id: string;
  /** The usual target (a TargetDef id). */
  target: string;
  /** The customer's variant of it, which is shown and judged (see Wish). */
  shape: Pick<TargetDef, "sdf" | "bound">;
  /** Who is asking, as signed at the end of the letter. */
  client: string;
  /**
   * The customer's face, by their name on the letter, the list of letters and the ledger,
   * and on the slip when they have a word on the piece.
   */
  face: Face;
  /** Short name of the order, for the list of letters. */
  title: string;
  /** What was ordered, as the delivery slip and the shelf label name it. */
  item: string;
  /**
   * What the shelf shows of it, on its label: the pieces there are all about one size, so
   * a hut stands as a scale model and a mug as a reference piece.
   */
  exhibit: string;
  /** The letter between its opening (拝啓 こねこ様) and its closing (敬具). */
  letter: string;
  /** A line after the signature (追伸), if the customer adds one. */
  postscript?: string;
  /** What the letter is written on: each customer has their own. */
  paper: Paper;
  wish: Wish;
  /** An air the letter hopes the piece has besides, if it asks for one. */
  atmosphere?: Atmosphere;
  /**
   * The customer's word on the delivery slip as the wish comes partway, then true: glad of
   * the piece, then thankful for just what they asked (of the wish alone: the air has its
   * own word).
   */
  thanks: [string, string];
  appearance: Appearance;
}

/** Display order: estimated difficulty within each hole count; unlock gates live in progression.ts. */
export const REQUESTS: RequestDef[] = [
  {
    id: "jomonyan-replica",
    target: "jomonyan",
    shape: { sdf: catHead(0.48), bound: 1.35 },
    client: "古墳保存会",
    face: {
      fur: "#cfc6ba",
      pattern: "plain",
      whiskers: "#a99d8f",
      wear: ["beard"],
      backdrop: "#eee4d2",
    },
    title: "ねこ頭形土製品の復元模型",
    item: "ねこ頭形土製品",
    exhibit: "復元模型",
    letter: `当会所蔵のねこ頭形土製品の復元模型をお願いしたく存じます。
丸いお顔と、ぴんとした二つのお耳がよく分かる形ですとうれしいです。
細かな模様まで写す必要はございません。まずはねこらしい姿にこねていただければ十分です。`,
    wish: {
      measure: "ears",
      label: "ねこらしい形状",
      hint: "頭の上を二か所つまんで、お耳を作ってみよう",
    },
    atmosphere: {
      label: "愛らしい・素朴",
      looksLike: ["a cute terracotta cat", "a small clay cat figurine"],
      thanks: [
        "愛らしくて、素朴なお顔です",
        "愛らしくて素朴で、土のぬくもりまで感じます",
      ],
      charmed: "思っていた姿とは違いますが、この素朴な愛らしさは捨てがたいです",
    },
    thanks: [
      "展示ケースに並べたら、見学の子ねこたちが「ご先祖さまだ」と喜んでいます",
      "たいへん愛らしい復元模型です。展示室の入口に飾らせていただきます",
    ],
    appearance: { ...DEFAULT_APPEARANCES.soil, color: 0xc8b89e },
    paper: "parchment",
  },
  {
    id: "nursery-snowman",
    target: "snowman",
    shape: { sdf: stretched(snowman, 1, 1 / 1.12), bound: 1.9 },
    client: "ふもとの保育園",
    face: {
      fur: "#fbf3e6",
      pattern: "calico",
      marks: ["#f0a860", "#5b4a44"],
      whiskers: "#b3a79e",
      wear: ["flower"],
      backdrop: "#fde8ec",
    },
    title: "お昼寝部屋の雪だるま",
    item: "雪だるまの置物",
    exhibit: "参考展示品",
    letter: `こんにちは、ふもとの保育園です。
お昼寝部屋に置く、雪だるまの置物をひとつお願いできますでしょうか。
子ねこが抱っこしても倒れないよう、ころんと低めの形だと安心です。
どうぞよろしくお願いいたします。`,
    wish: { measure: "aspect", label: "ころんと低め", hint: "背を低くして、横に丸くしてみよう" },
    atmosphere: {
      label: "やさしそう",
      looksLike: ["a cute snowman", "a chubby snowman"],
      thanks: ["やさしそうで、安心です", "やさしそうで、子ねこもにっこりです"],
      charmed: "思っていた形と違うけど、子ねこたちは大喜びです！",
    },
    thanks: [
      "お昼寝部屋に置いたら、子ねこたちがそばで丸くなって眠っています",
      "ころんとしていて、抱っこしても倒れません。お昼寝のお供に大人気です",
    ],
    appearance: { ...DEFAULT_APPEARANCES.snow, finish: "damp" },
    paper: "crayon",
  },
  {
    id: "festival-snowman",
    target: "snowman",
    shape: { sdf: stretched(snowman, 1, 1.15), bound: 2 },
    client: "北の村 子ねこ会",
    face: {
      fur: "#fbf8f3",
      pattern: "plain",
      whiskers: "#b3a79e",
      wear: ["beanie"],
      backdrop: "#e3eff9",
    },
    title: "冬まつりの雪だるま",
    item: "背の高い雪だるま",
    exhibit: "1/12 縮尺模型",
    letter: `寒さが身にしみる季節となりました。
今年の冬まつりで、広場のまん中に飾る雪だるまをお願いしたく、お手紙いたしました。
遠くからでも見えるよう、背の高い雪だるまにしていただけるとうれしいです。
子ねこ一同、楽しみにお待ちしております。`,
    wish: { measure: "aspect", label: "背の高さ", hint: "縦に伸ばして、背を高くしてみよう" },
    atmosphere: {
      label: "元気そう",
      looksLike: ["a tall snowman", "a cheerful snowman"],
      thanks: ["元気そうで、うれしいです", "見るからに元気いっぱいです"],
      charmed: "思ってたのとちがうけど、見てるだけで元気が出ます！",
    },
    thanks: [
      "広場に飾ったら、子ねこたちがまわりで雪合戦を始めました",
      "背が高くて、広場のどこからでも見えました！ 子ねこたちも大はしゃぎです",
    ],
    appearance: { ...DEFAULT_APPEARANCES.snow, finish: "packed" },
    paper: "snowKids",
  },
  {
    id: "grocer-peanut",
    target: "peanut",
    shape: { sdf: stretched(peanut, 0, 1 / 1.12), bound: 1.7 },
    client: "豆問屋 まめ吉",
    face: {
      fur: "#d9a56f",
      pattern: "tabby",
      marks: ["#b07845"],
      wear: ["huntingCap"],
      backdrop: "#f1e2c8",
    },
    title: "店先の看板ピーナッツ",
    item: "看板のピーナッツ",
    exhibit: "1/5 縮尺模型",
    letter: `いつもお世話になっております。
このたび店先の看板を新しくすることになり、ピーナッツの置物をお願いしたく存じます。
食べごたえがありそうに見えるよう、ずんぐりと太った形だとうれしいです。
どうぞよろしくお願いいたします。`,
    wish: { measure: "aspect", label: "ずんぐり", hint: "長さを詰めて、太めにしてみよう" },
    atmosphere: {
      label: "親しみやすい",
      looksLike: ["a plump peanut", "a smiling peanut"],
      thanks: ["親しみやすくて、いいですね", "親しみやすくて、すっかり店の顔です"],
      charmed: "イメージとは違いますが、なんだか憎めなくて、気に入りました",
    },
    thanks: [
      "店先に置いたら、通りがかりのねこが足を止めてくれるようになりました",
      "ずんぐりしていて、いかにもおいしそうです。おかげでお客さんが増えました",
    ],
    appearance: { ...DEFAULT_APPEARANCES.ceramic, color: 0x9a5522 },
    paper: "kraft",
  },
  {
    id: "brewery-gourd",
    target: "gourd",
    shape: { sdf: gourd(0.18), bound: 1.8 },
    client: "山の酒蔵",
    face: {
      fur: "#b8a38e",
      pattern: "tabby",
      marks: ["#8a7563"],
      wear: ["hachimaki"],
      backdrop: "#f3e9d6",
    },
    title: "山歩きのひょうたん",
    item: "ひょうたん",
    exhibit: "参考展示品",
    letter: `新緑の候、ますますご活躍のことと存じます。
このたび、山歩きのお客様にお酒をお分けするひょうたんを、ひとつ探しております。
腰にひもでしっかり結べるよう、くびれの深い形だとありがたいです。
お手すきの折に、こねていただけますと幸いです。`,
    wish: { measure: "waist", label: "深いくびれ", hint: "くびれをもっと深く絞ってみよう" },
    atmosphere: {
      label: "素朴で味わい深い",
      looksLike: ["a gourd", "a gourd bottle"],
      thanks: ["素朴な味わいがありますね", "素朴で、使うほど味が出そうです"],
      charmed: "思っていた形とは違いますが、これはこれで味があります",
    },
    thanks: [
      "よいひょうたんをありがとうございます。さっそくお酒を詰めて、腰に下げてみました",
      "くびれが深くて、ひもがしっかり結べました。これで山道でも落としません",
    ],
    appearance: { ...DEFAULT_APPEARANCES.ceramic, color: 0xb98252 },
    paper: "washi",
  },
  {
    id: "lottery-bell",
    target: "bell",
    shape: { sdf: bell(0.24), bound: 1.5 },
    client: "ねこ町商店街 振興会",
    face: {
      fur: "#fbf8f3",
      pattern: "hachiware",
      marks: ["#4d4448"],
      whiskers: "#b3a79e",
      wear: ["mustache"],
      backdrop: "#fde6d2",
    },
    title: "福引の鐘",
    item: "福引の鐘",
    exhibit: "参考展示品",
    letter: `商店街の福引で使う鐘をお願い申し上げます。
よく目立ち、持ち手がしっかりしているものを希望します。
鳴らすたびに、めでたい気分になれる鐘だとうれしいです。`,
    wish: { measure: "waist", label: "しっかりした持ち手", hint: "持ち手をもっと太くしてみよう" },
    atmosphere: {
      label: "めでたそう",
      looksLike: ["a celebration bell", "a hand bell"],
      thanks: ["なんだかめでたい感じです", "見るからにめでたくて、縁起がいいです"],
      charmed: "想像とは違いましたが、見ているだけで当たりそうな気がします",
    },
    thanks: [
      "鳴らしてみたら、商店街じゅうに響きました。福引の日が待ち遠しいです",
      "持ち手がしっかりしていて、大当たりのたびに思いきり鳴らせます",
    ],
    appearance: { ...DEFAULT_APPEARANCES.metal, color: 0xc8d1dc },
    paper: "festive",
  },
  {
    id: "cafe-mug",
    target: "mug",
    shape: { sdf: mug(0.6), bound: 1.7 },
    client: "町の喫茶店",
    face: {
      fur: "#f3e6d2",
      pattern: "points",
      marks: ["#6b4a3a"],
      eyes: "#3f6fa8",
      wear: ["bowTie"],
      backdrop: "#efe2d3",
    },
    title: "お店のマグカップ",
    item: "マグカップ",
    exhibit: "参考展示品",
    letter: `いつも見事なこねこね、ありがとうございます。
このたび、お店で使うマグカップがひとつ足りなくなってしまいました。
子ねこでも持ちやすいように、取っ手が大きな形だとうれしいです。
どうか、よい感じにこねていただけないでしょうか。`,
    wish: { measure: "hole", label: "大きな取っ手", hint: "取っ手の穴を大きめにしてみよう" },
    atmosphere: {
      label: "あたたかそう",
      looksLike: ["a coffee mug", "a cozy mug"],
      thanks: ["どこかぬくもりがあります", "ぬくもりが伝わってきて、ほっとします"],
      charmed: "形は少し違いますが、あたたかみのある素敵なカップですね",
    },
    thanks: [
      "さっそくお店で使っています。温かいミルクがよく似合います",
      "取っ手が大きくて助かりました。これなら子ねこでも両手で持てそうです",
    ],
    appearance: { ...DEFAULT_APPEARANCES.ceramic, color: 0xd98f66 },
    paper: "cafe",
  },
  {
    id: "grocer-bean-loop",
    target: "beanLoop",
    shape: { sdf: beanLoop(0.4), bound: 1.6 },
    client: "豆問屋 まめ吉",
    face: {
      fur: "#d9a56f",
      pattern: "tabby",
      marks: ["#b07845"],
      wear: ["huntingCap"],
      backdrop: "#f1e2c8",
    },
    title: "豆シルエットのオブジェ",
    item: "豆のオブジェ",
    exhibit: "参考展示品",
    letter: `いつもお世話になっております。
今度の豆まつりで、店の入口に飾るオブジェをお願いしたく存じます。
豆のかたちを輪にした、すかし模様のようなものを考えております。
まん中がきゅっとくびれて、ひと目で豆と分かるとうれしいです。`,
    wish: {
      measure: "neck",
      label: "中央のくびれ",
      hint: "まん中を上下からきゅっと絞って、くびれを深くしてみよう",
    },
    atmosphere: {
      label: "親しみやすい",
      looksLike: ["a peanut sculpture", "a plump peanut"],
      thanks: [
        "どこか親しみがあって、ほっとします",
        "親しみやすくて、お客さんがみんな触っていきます",
      ],
      charmed: "頼んだ形とは違うけど、なんだか愛着がわいてきました",
    },
    thanks: [
      "入口に飾ったら、豆まつりの目印になりました",
      "まん中のくびれがきゅっとして、遠くからでもお豆だと分かります",
    ],
    appearance: { material: "ceramic", finish: "matte", color: 0x9a5a2e },
    paper: "kraft",
  },
  {
    id: "snow-kamakura",
    target: "kamakura",
    shape: { sdf: kamakura(0.3), bound: 1.5 },
    client: "雪まつり実行委員会",
    face: {
      fur: "#a9b8c8",
      pattern: "plain",
      whiskers: "#8a96a3",
      wear: ["earmuffs"],
      backdrop: "#dfeaf6",
    },
    title: "おおきなかまくら",
    item: "おおきなかまくら",
    exhibit: "1/20 縮尺模型",
    letter: `雪原ねこ町にて、冬の展示用におおきなかまくらを用意したく存じます。
あたたかな囲炉裏の煙を逃がせるよう、上には煙突をひとつ。
丸みのある、やさしい形だとうれしいです。`,
    wish: {
      measure: "aspect",
      label: "こんもりした形",
      hint: "上へこんもり盛って、丸くしてみよう",
    },
    atmosphere: {
      label: "やさしい丸み",
      looksLike: ["an igloo"],
      thanks: ["やさしい丸みに、ほっとします", "やさしい丸みに、みんなうっとりです"],
      charmed: "思い描いた形とは違いますが、この丸みに、みんなほっこりしています",
    },
    thanks: [
      "会場に据えたら、子ねこがもぐりこんでいます",
      "こんもり丸くて、中は広くてぽかぽかです",
    ],
    appearance: { ...DEFAULT_APPEARANCES.snow, color: 0xf4f8fc },
    paper: "letterhead",
  },
  {
    id: "park-can",
    target: "wateringCan",
    shape: { sdf: wateringCan(0.52), bound: 1.4 },
    client: "ひだまり公園 管理事務所",
    face: {
      fur: "#aeb4bb",
      pattern: "tabby",
      marks: ["#8d949c"],
      whiskers: "#8a8f96",
      wear: ["strawHat"],
      backdrop: "#e2f1e4",
    },
    title: "水飲み場のじょうろ",
    item: "ねこ用のじょうろ",
    exhibit: "参考展示品",
    letter: `公園の水飲み場に置く、ねこ用の低いじょうろをお願いしたく存じます。
持ち手は大きめ、口は少し上向きですと助かります。
水を飲みに来たねこたちが、ついでに花壇のお花にも水をあげられたらうれしいです。`,
    wish: { measure: "hole", label: "大きな持ち手", hint: "持ち手の輪を大きくしてみよう" },
    atmosphere: {
      label: "使いやすそう",
      looksLike: ["an easy-to-use watering can", "a handy watering can"],
      thanks: ["なかなか使いやすそうです", "とても使いやすくて、重宝しています"],
      charmed: "思っていた形とは違いますが、これが案外使いやすいんです",
    },
    thanks: [
      "水飲み場に置いたら、ねこたちが代わる代わるのぞきに来ています",
      "持ち手が大きくて、肉球でもつかみやすいです。水飲み場がにぎやかになりました",
    ],
    appearance: { ...DEFAULT_APPEARANCES.plastic, color: 0x3fae5c },
    paper: "grid",
  },
  {
    id: "bakery-cornet",
    target: "cornet",
    shape: { sdf: cornet(1.3), bound: 1.7 },
    client: "ねこベーカリー 店主",
    face: {
      fur: "#efd2a4",
      pattern: "tabby",
      marks: ["#d9a86a"],
      wear: ["chefHat"],
      backdrop: "#fbeede",
    },
    title: "チョココロネのコロネ",
    item: "チョココロネのコロネ",
    exhibit: "食品見本",
    letter: `先日、店頭に飾っていたチョココロネのコロネの部分を、うっかり割ってしまいました。
せっかく作り直すのであれば、以前よりも少し立派で、くるくるがよく見えるものをお願いできればと思っております。
中身はこちらで用意いたしますので、ご心配には及びません。`,
    postscript: "食品見本の話です。",
    wish: {
      measure: "coils",
      label: "くっきりした巻き",
      hint: "巻きと巻きの間のみぞを深くしてみよう",
    },
    atmosphere: {
      label: "おいしそう",
      looksLike: ["freshly baked bread", "a croissant"],
      thanks: ["おいしそうだと好評です", "本物よりおいしそうだと評判です"],
      charmed: "注文とは違うけど、おいしそうで、つい気に入ってしまいました",
    },
    thanks: [
      "店頭にコロネが戻って、常連さんも一安心です",
      "くるくるがくっきりして、店頭でよく目立ちます",
    ],
    appearance: { ...DEFAULT_APPEARANCES.bread, color: 0xd0924a },
    paper: "bakery",
  },
  {
    id: "future-square-mug",
    target: "squareMug",
    shape: { sdf: squareMug(1), bound: 1.4 },
    client: "近未来同好会",
    face: {
      fur: "#d5dbe3",
      pattern: "plain",
      eyes: "#3f8fd2",
      whiskers: "#a3adb9",
      wear: ["glasses"],
      backdrop: "#e1e9f5",
    },
    title: "未来喫茶の角張りマグ",
    item: "角張りマグ",
    exhibit: "参考展示品",
    letter: `未来文化祭にて、一日だけの「未来喫茶」を開くことになりました。
そこで使う、未来っぽいマグをひとつお願いしたく存じます。
胴は角張って、取っ手もシャープに角を立てていただけますと幸いです。
丸いマグは、いったん過去に置いてまいります。`,
    postscript: "ろくろより、平らにする道具が向いているかもしれません。",
    wish: {
      measure: "square",
      label: "きりっとした角",
      hint: "「平ら」で四方の面を整えて、角を立ててみよう",
    },
    atmosphere: {
      label: "未来っぽい",
      looksLike: ["a futuristic mug", "a sleek modern cup"],
      thanks: ["どことなく未来の香りがします", "これはもう、未来から届いたマグです"],
      charmed: "設計図とは違う……しかし、これは未来だ！",
    },
    thanks: [
      "未来喫茶で使ってみたら、お客さんがみんな写真を撮っていきました",
      "角がきりっと立って、取っ手までシャープです。まさに未来の器です",
    ],
    appearance: { ...DEFAULT_APPEARANCES.plastic, color: 0x24282f },
    paper: "future",
  },
  {
    id: "boutique-sunglasses",
    target: "sunglasses",
    shape: { sdf: sunglasses(0.27), bound: 1.5 },
    client: "ねこ町ブティック",
    face: {
      fur: "#ebe1e7",
      pattern: "plain",
      eyes: "#4e9a78",
      whiskers: "#b3a0ad",
      wear: ["sunglasses"],
      backdrop: "#fbe3ee",
    },
    title: "イケてるサングラス",
    item: "ねこみみサングラス",
    exhibit: "参考展示品",
    letter: `来週の商店街ファッションまつりに向けて、イケてるサングラスを一品お願いしたく存じます。
できれば、ひと目でねこと分かるよう、上のあたりにねこみみがついているとうれしいです。
かっこよさの中にも、ほんのり愛らしさがございますと幸いです。`,
    wish: {
      measure: "ears",
      label: "ねこみみ",
      hint: "フレームの上に、ねこみみをつまみ出してみよう",
    },
    atmosphere: {
      label: "イケてる",
      looksLike: ["cool sunglasses", "stylish sunglasses"],
      thanks: ["なかなかイケていますね", "想像以上にイケていて、看板ねこもご満悦でした"],
      charmed: "イメージとは少し違うけど、これはこれでイケてますね！",
    },
    thanks: [
      "さっそくショーウィンドウに飾りました。道ゆくねこがみんな振り返ります",
      "遠くから見ても、ちゃんとねこみみが分かってうれしいです",
    ],
    appearance: { ...DEFAULT_APPEARANCES.plastic, color: 0x252b33 },
    paper: "boutique",
  },
  {
    id: "grocer-face-panel",
    target: "peanutPanel",
    shape: { sdf: peanutPanel(0.6), bound: 1.5 },
    client: "豆問屋 まめ吉",
    face: {
      fur: "#d9a56f",
      pattern: "tabby",
      marks: ["#b07845"],
      wear: ["huntingCap"],
      backdrop: "#f1e2c8",
    },
    title: "ピーナッツ型の顔はめパネル",
    item: "顔はめパネル",
    exhibit: "1/8 縮尺模型",
    letter: `たびたびお世話になります。
豆まつりの記念撮影用に、ピーナッツ型の顔はめパネルを作ることになりました。
背丈の違うふたりで顔を出せるよう、穴はふたつ、殻の左右にひとつずつお願いします。
見本のように、片方は低め、もう片方は高めにしてください。
ピーナッツらしく、まん中のくびれもしっかりあるとうれしいです。`,
    postscript: "まずは模型で、仲間に見せて回りたいと思います。",
    wish: {
      measure: "neck",
      label: "ピーナッツのくびれ",
      hint: "穴の中をつまんで高さを変えたら、穴の間を上下から絞ってくびれを深くしてみよう",
    },
    atmosphere: {
      label: "たのしそう",
      looksLike: ["a face-in-hole photo board", "a fun carnival photo board"],
      thanks: ["なんだか楽しそうです", "見ているだけで、楽しくなってきます"],
      charmed: "思っていた形とは違うけど、なんだか楽しくて、みんな気に入っています",
    },
    thanks: [
      "模型を見せたら、仲間がみんな顔を近づけてのぞきこんでいました",
      "顔穴がちょうどよくて、くびれもいかにもピーナッツです。本番が楽しみです",
    ],
    appearance: { ...DEFAULT_APPEARANCES.wood, color: 0xd9a066 },
    paper: "kraft",
  },
  {
    id: "tumulus-trousers",
    target: "trousers",
    shape: { sdf: trousers(0.12), bound: 1.5 },
    client: "古墳保存会",
    face: {
      fur: "#cfc6ba",
      pattern: "plain",
      whiskers: "#a99d8f",
      wear: ["beard"],
      backdrop: "#eee4d2",
    },
    title: "はにわのおズボン",
    item: "はにわのおズボン",
    exhibit: "1/3 復元模型",
    letter: `古墳まつりの展示用に、はにわのおズボンをお願いしたく存じます。
丈夫で、左右の脚がしっかり分かれているとうれしいです。
できれば少し、古代らしい趣もございますと幸いです。`,
    wish: { measure: "split", label: "分かれた脚", hint: "左右の脚の間をもっと空けてみよう" },
    atmosphere: {
      label: "古代感",
      looksLike: ["a terracotta figure", "an ancient terracotta figure"],
      thanks: ["古代の息吹を感じます", "古代の趣はばっちりです"],
      charmed: "思っていたおズボンとは違いますが、古代の風格がただよっています",
    },
    thanks: [
      "はにわに履かせたら、ぴったりでした。展示が楽しみです",
      "脚が分かれて、はにわもすっくと立てました",
    ],
    appearance: { ...DEFAULT_APPEARANCES.soil, color: 0xb98252 },
    paper: "parchment",
  },
  {
    id: "tumulus-mask",
    target: "haniwaMask",
    shape: { sdf: haniwaMask(0.07), bound: 1.3 },
    client: "古墳保存会",
    face: {
      fur: "#cfc6ba",
      pattern: "plain",
      whiskers: "#a99d8f",
      wear: ["beard"],
      backdrop: "#eee4d2",
    },
    title: "はにわのお面",
    item: "はにわのお面",
    exhibit: "参考展示品",
    letter: `来月の古墳まつりにて飾る、はにわ風のお面を一品お願いしたく存じます。
目はふたつ、口はひとつ。口は縦に大きく、堂々と開けてください。
どこか素朴で、古代の気配を感じるものですと幸いです。`,
    wish: { measure: "hole", label: "堂々とした口", hint: "口の穴を縦に大きく開けてみよう" },
    atmosphere: {
      label: "素朴・古代の気配",
      looksLike: ["a haniwa face", "an ancient terracotta mask"],
      thanks: ["素朴で、どこか古代の気配がします", "素朴で懐かしく、古代の気配がただよっています"],
      charmed: "思っていたお顔とは違いますが、これぞ古代という気配がします",
    },
    thanks: [
      "お目々の配置がよく、たいへん愛嬌のあるお面です",
      "口の開きが堂々としていて、古代らしい迫力があります",
    ],
    appearance: { ...DEFAULT_APPEARANCES.soil, color: 0xb98252 },
    paper: "parchment",
  },
  {
    id: "bakery-pretzel",
    target: "pretzel",
    shape: { sdf: pretzel(1.15), bound: 1.5 },
    client: "ねこベーカリー 店主",
    face: {
      fur: "#efd2a4",
      pattern: "tabby",
      marks: ["#d9a86a"],
      wear: ["chefHat"],
      backdrop: "#fbeede",
    },
    title: "カリカリのプレッツェル",
    item: "プレッツェル",
    exhibit: "食品見本",
    letter: `パンまつりに向けて、プレッツェルの食品見本をお願いしたく存じます。
ひもを結んだようなあの形で、まん中の交差とふたつの輪がよく分かるとうれしいです。
こんがり香ばしく見えるよう、焼きっぱなしの仕上げでお願いいたします。`,
    postscript: "塩粒はこちらで振っておきます。",
    wish: {
      measure: "hole",
      label: "大きな輪",
      hint: "みっつの穴を大きく開けて、交差をくっきりさせてみよう",
    },
    atmosphere: {
      label: "香ばしそう",
      looksLike: ["a pretzel", "a freshly baked pretzel"],
      thanks: ["香ばしい匂いがしてきそうです", "焼きたての香りまで伝わってきます"],
      charmed: "注文とは違うけど、香ばしそうで、思わず店に並べたくなりました",
    },
    thanks: [
      "店頭に並べたら、本物とまちがえて注文されました",
      "交差もふたつの輪もくっきりして、ひと目でプレッツェルです",
    ],
    appearance: { ...DEFAULT_APPEARANCES.bread, finish: "toasted", color: 0xa8612a },
    paper: "bakery",
  },
  {
    id: "future-outlet-plate",
    target: "outletPlate",
    shape: { sdf: outletPlate(0.08), bound: 1.2 },
    client: "近未来同好会",
    face: {
      fur: "#d5dbe3",
      pattern: "plain",
      eyes: "#3f8fd2",
      whiskers: "#a3adb9",
      wear: ["glasses"],
      backdrop: "#e1e9f5",
    },
    title: "3ピンコンセントの飾り板",
    item: "コンセントの飾り板",
    exhibit: "展示用模型",
    letter: `来月の未来文化祭にて展示する、三つ穴のコンセント飾り板をお願いしたく存じます。
穴の並びがきれいで、ひと目でそれと分かる見た目ですと助かります。
なるべくすっきりと、少し先進的な雰囲気がございますと幸いです。`,
    postscript: "通電の予定はございません。展示用です。",
    wish: {
      measure: "spread",
      label: "そろった三つ穴",
      hint: "三つの穴の位置関係をそろえ、外枠を平たく整えてみよう",
    },
    atmosphere: {
      label: "未来っぽい",
      looksLike: ["a wall power socket", "a futuristic power outlet"],
      thanks: ["すっきりしていて、どこか未来っぽいです", "先進的で、未来っぽさがよく出ています"],
      charmed: "仕様書とは異なる……だが、この未来感は認めざるを得ない！",
    },
    thanks: [
      "会場に飾ったら、未来の家電みたいだと見物のねこが集まってきました",
      "穴の配置がきれいで、たいへん未来的です。会場の銀色の展示台にもよく合いそうです",
    ],
    appearance: { ...DEFAULT_APPEARANCES.plastic, color: 0xf1f2ee },
    paper: "future",
  },
  {
    id: "kitchen-onigiri-mold",
    target: "onigiriMold",
    shape: { sdf: onigiriMold(0.12), bound: 1.6 },
    client: "ねこ台所用品店",
    face: {
      fur: "#f6efe4",
      pattern: "calico",
      marks: ["#e39a54", "#4f4440"],
      whiskers: "#b3a79e",
      wear: ["hachimaki"],
      backdrop: "#e3f2e2",
    },
    title: "△▽△ おにぎりの型",
    item: "おにぎりの型",
    exhibit: "参考展示品",
    letter: `行楽フェアの売り場に、おにぎりの型を並べたく、お便りいたしました。
三角の穴をみっつ、上・下・上と互い違いに、横一列にお願いいたします。
穴の大きさと間隔がきっちりそろっていると、売り場でも映えると存じます。`,
    wish: {
      measure: "spread",
      label: "等間隔の三角穴",
      hint: "みっつの三角の穴を、間をそろえて横に広げてみよう",
    },
    atmosphere: {
      label: "きっちりしている",
      looksLike: ["a rice ball mold", "a neat kitchen mold"],
      thanks: ["きっちりしていて、気持ちがいいです", "寸分の狂いもない、きっちりした仕事です"],
      charmed: "思っていた型とは違いますが、きっちりした仕事ぶりに惚れました",
    },
    thanks: [
      "売り場に並べたら、行楽前のお客さんが手に取ってくださいました",
      "三角の穴がきっちりそろって、おにぎりの形まで整いそうです",
    ],
    appearance: { ...DEFAULT_APPEARANCES.plastic, color: 0x3fae5c },
    paper: "kitchen",
  },
  {
    id: "craft-big-button",
    target: "bigButton",
    shape: { sdf: bigButton(0.07), bound: 1.3 },
    client: "手芸店 ぬいぬい堂",
    face: {
      fur: "#fbf3e6",
      pattern: "calico",
      marks: ["#d98a4e", "#6b5a52"],
      whiskers: "#b3a79e",
      wear: ["flower"],
      backdrop: "#fbe6ea",
    },
    title: "4つ穴の大きなボタン",
    item: "四つ穴ボタン",
    exhibit: "展示用見本",
    letter: `今度の手づくり市に飾る、大きな四つ穴ボタンの見本をお願いしたく存じます。
穴は四つ、なるべく均等に並び、見た目にも整っておりますと助かります。
ころんとしていて、どこか愛らしい雰囲気ですとうれしく思います。`,
    postscript: "実際に縫いつける予定はございません。展示用見本です。",
    wish: {
      measure: "spread",
      label: "整った四つ穴",
      hint: "四つの穴を正方形っぽくそろえ、外周も丸く整えてみよう",
    },
    atmosphere: {
      label: "かわいらしい",
      looksLike: ["a big wooden button", "a cute sewing button"],
      thanks: [
        "どこかかわいらしい仕上がりです",
        "ころんとして、見ているだけで和むかわいらしさです",
      ],
      charmed: "思っていたボタンとは違うけど、かわいくて、つい手に取ってしまいます",
    },
    thanks: [
      "店先に飾ったら、お客さんがみんな糸を通してみたがっていました",
      "四つの穴がきれいで、とても立派な見本になりました。手づくり市でもよく目立ちそうです",
    ],
    appearance: { ...DEFAULT_APPEARANCES.wood, color: 0x9a6236 },
    paper: "stitch",
  },
  {
    id: "archive-window-frame",
    target: "windowFrame",
    shape: { sdf: windowFrame(0.05), bound: 1.2 },
    client: "森の資料室",
    face: {
      fur: "#a88b6e",
      pattern: "tabby",
      marks: ["#7d6450"],
      whiskers: "#8f7a66",
      wear: ["glasses"],
      backdrop: "#e3eedb",
    },
    title: "木の四つ窓枠",
    item: "四つ窓枠",
    exhibit: "1/6 縮尺模型",
    letter: `山小屋の暮らしを紹介する展示で、窓まわりの模型をお願いしたく存じます。
四つの窓が田の字に並び、まん中の桟が縦にも横にも細く、まっすぐ通っている窓枠です。
丁寧なお仕事を、楽しみにお待ちしております。`,
    wish: {
      measure: "hole",
      label: "細くまっすぐな桟",
      hint: "「平ら」と「ならす」で桟を細くまっすぐ整え、窓をそろえて広げてみよう",
    },
    atmosphere: {
      label: "丁寧な仕事",
      looksLike: ["a wooden window frame", "a neat wooden lattice window"],
      thanks: ["丁寧なお仕事ぶりが伝わります", "隅々まで丁寧で、資料室の宝になります"],
      charmed: "図面とは違いますが、丁寧な仕事ぶりに、思わず見入ってしまいました",
    },
    thanks: [
      "展示室に置いたら、山小屋の窓辺が目に浮かぶと評判です",
      "四つの窓がそろって、桟もまっすぐ通っています。まさに職人の仕事です",
    ],
    appearance: { ...DEFAULT_APPEARANCES.wood, finish: "unpainted", color: 0xc89868 },
    paper: "archive",
  },
  {
    id: "tumulus-kirin",
    target: "kirin",
    shape: { sdf: kirin(0.12), bound: 1.4 },
    client: "古墳保存会",
    face: {
      fur: "#cfc6ba",
      pattern: "plain",
      whiskers: "#a99d8f",
      wear: ["beard"],
      backdrop: "#eee4d2",
    },
    title: "四駆のキリン",
    item: "キリンのはにわ",
    exhibit: "復元模型",
    letter: `古墳まつりの目玉に、キリンのはにわの復元模型をお願いしたく存じます。
長い首と、四本の脚がしっかり分かれて踏んばっている姿ですと幸いです。
出土した品の絵を同封いたしますので、おてほんにしていただけますと助かります。`,
    postscript: "絵の脚の先が丸いのは、たぶん気のせいでございます。",
    wish: {
      measure: "split",
      label: "分かれた四本脚",
      hint: "前と後ろの脚（と車輪）の間を、もっと空けてみよう",
    },
    atmosphere: {
      label: "古代の趣",
      looksLike: ["an ancient terracotta toy", "a terracotta giraffe on wheels"],
      thanks: ["古代の趣を感じます", "古代の息吹が、車輪の音とともに伝わってきます"],
      charmed: "思っていたキリンとは違いますが、古代の趣があって、これも一興です",
    },
    thanks: [
      "展示したら、子ねこたちが押して走らせたがっています",
      "四本の脚がしっかり分かれて、車輪もよく回りそうです。古代の人もきっとこれで遊んだのでしょう",
    ],
    appearance: { ...DEFAULT_APPEARANCES.soil, color: 0xc8b89e },
    paper: "parchment",
  },
];
